import { Box, Checkbox, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import type { SearchMarket } from '../api/searchTypes';
import { gridTemplate, resultColumns } from './resultColumns';
import { RawDataPopover } from './RawDataPopover';
import { RowImage } from './RowImage';
import { StatusCell } from './StatusCell';
import { TagChip, type TagState } from './TagChip';

export interface MarketRowProps {
    market: SearchMarket;
    tagState: (tag: string) => TagState;
    selected: boolean;
    chartOpen: boolean;
    showRaw: boolean;
    onIncludeTag: (tag: string) => void;
    onExcludeTag: (tag: string) => void;
    onToggleSelect: (marketId: string) => void;
    onToggleChart: (marketId: string) => void;
}

export function MarketRow({
    market,
    tagState,
    selected,
    chartOpen,
    showRaw,
    onIncludeTag,
    onExcludeTag,
    onToggleSelect,
    onToggleChart,
}: MarketRowProps) {
    const theme = useTheme();
    const columns = resultColumns();
    const outcome =
        market.group_item_title ?? market.outcome_yes_label ?? 'Yes';

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplate(),
                columnGap: 1,
                alignItems: 'center',
                px: 1.25,
                py: 0.6,
                borderTop: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.5),
                opacity: market.matched ? 1 : 0.5,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, pl: 4.5 }}>
                <RowImage src={market.market_image} size={20} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {market.question ?? market.market_id}
                    </Typography>
                    {market.tags.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.3,
                                flexWrap: 'wrap',
                            }}
                        >
                            {market.tags.map(tag => (
                                <TagChip
                                    key={tag}
                                    label={tag}
                                    state={tagState(tag)}
                                    onInclude={onIncludeTag}
                                    onExclude={onExcludeTag}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>

            {columns.map(column => {
                if (column.key === 'status') {
                    return <StatusCell key={column.key} status={market.status} />;
                }

                if (column.key === 'price') {
                    return (
                        <Box
                            key={column.key}
                            sx={{
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '11px',
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.2,
                                }}
                            >
                                {column.marketValue(market)}
                            </Typography>
                            <Tooltip title={outcome} placement="top" disableInteractive>
                                <Typography
                                    sx={{
                                        fontSize: '10px',
                                        color: 'text.disabled',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%',
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {outcome}
                                </Typography>
                            </Tooltip>
                        </Box>
                    );
                }

                return (
                    <Typography
                        key={column.key}
                        sx={{
                            fontSize: '11px',
                            textAlign: column.align === 'left' ? 'left' : 'right',
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {column.marketValue(market)}
                    </Typography>
                );
            })}

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '2px',
                }}
            >
                {showRaw && <RawDataPopover marketId={market.market_id} />}
                <Tooltip title="Show candle chart" disableInteractive enterDelay={600}>
                    <Box
                        onClick={e => {
                            e.stopPropagation();
                            onToggleChart(market.market_id);
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 20,
                            height: 20,
                            borderRadius: '3px',
                            cursor: 'pointer',
                            color: chartOpen ? 'primary.main' : 'text.disabled',
                            bgcolor: chartOpen
                                ? alpha(theme.palette.primary.main, 0.12)
                                : 'transparent',
                            '&:hover': { color: 'primary.main' },
                        }}
                    >
                        <CandlestickChartIcon sx={{ fontSize: 13 }} />
                    </Box>
                </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Checkbox
                    size="small"
                    checked={selected}
                    onClick={e => e.stopPropagation()}
                    onChange={() => onToggleSelect(market.market_id)}
                    sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: 15 } }}
                />
            </Box>
        </Box>
    );
}